import { Component, Input, OnChanges, OnDestroy, OnInit, SimpleChanges } from '@angular/core';
import { ChangeDetectionStrategy, ChangeDetectorRef } from '@angular/core';

import { Subscription } from 'rxjs';

import { languageService } from '@unpispas/upp-base';
import { ViewRenderer } from '@unpispas/upp-wdgt';
import { dataService } from '@unpispas/upp-data';
import { PlaceView } from '@unpispas/upp-data';
import { QrCodeView } from '@unpispas/upp-data';
import { TicketView } from '@unpispas/upp-data';

import { UppPinpadWorkComponent } from '@unpispas/feature/pinpad';

/**
 * PTF Tickets tab: open tickets via Work.`OpenTickets` (optional mesa filter).
 * OnPush + cached recent/past; past thumbs mount after the first paint.
 * Composed by `PtfPlaceShellComponent` (not inside `feature/pinpad`).
 */
@Component({
    selector: 'app-ptf-tickets',
    templateUrl: './ptf-tickets.html',
    styleUrl: './ptf-tickets.scss',
    changeDetection: ChangeDetectionStrategy.OnPush,
    providers: [{
        provide: ViewRenderer,
        useFactory: (change: ChangeDetectorRef) => new ViewRenderer(change),
        deps: [ChangeDetectorRef],
    }],
})
export class PtfTicketsComponent implements OnInit, OnChanges, OnDestroy {
    /** Host work shell (OpenTickets / TableFilter). */
    @Input() work: UppPinpadWorkComponent | null = null;

    private _placeSub: Subscription | null = null;
    /** Rebuild open recent/past caches on next getter read. */
    private _listDirty = true;
    private _recent: Array<TicketView> = [];
    private _past: Array<TicketView> = [];
    /** Past section stays out of the first paint (recent thumbs first). */
    private _pastMounted = false;
    private _pastTimer: ReturnType<typeof setTimeout> | null = null;

    constructor(
        private lang: languageService,
        private data: dataService,
        private change: ViewRenderer,
    ) {}

    get place(): PlaceView | null {
        return this.data.alives.get(this.data.place) as PlaceView;
    }

    /** Open tickets for the place or the mesa in TableFilter (DD-03-05). */
    get tickets(): Array<TicketView> {
        this._RebuildLists();
        return this._recent.concat(this._past);
    }

    get RecentTickets(): Array<TicketView> {
        this._RebuildLists();
        return this._recent;
    }

    get PastTickets(): Array<TicketView> {
        this._RebuildLists();
        return this._past;
    }

    /** Past block DOM is deferred until after recent has a chance to paint. */
    get ShowPastSection(): boolean {
        return this._pastMounted && this.PastTickets.length > 0;
    }

    /** Same as POS tab-tickets `IsEmptyView`: no recent open tickets. */
    get IsEmptyRecent(): boolean {
        return this.RecentTickets.length === 0;
    }

    get TableFilter(): QrCodeView | null {
        return this.work?.TableFilter ?? null;
    }

    get HasTableFilter(): boolean {
        return this.TableFilter !== null;
    }

    get FilterLabel(): string {
        const table = this.TableFilter;
        if (!table) {
            return '';
        }
        return this.lang.tr('@ptf_tickets_filter_table', [table.number ?? '']);
    }

    get Title(): string {
        const count = this.RecentTickets.length;
        if (count === 0) {
            return this.lang.tr('@place_tickets_empty_title');
        }
        return this.lang.tr('@place_tickets_title', [count]);
    }

    tr(key: string, args: unknown[] = []): string {
        return this.lang.tr(key, args as never);
    }

    TicketTrack(_index: number, ticket: TicketView): string | null {
        return ticket?._uuid || null;
    }

    ngOnInit(): void {
        this._BindPlaceRefresh();
        this._SchedulePastMount();
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['work']) {
            this._listDirty = true;
            this.change.markForCheck();
        }
    }

    ngOnDestroy(): void {
        if (this._placeSub) {
            this._placeSub.unsubscribe();
            this._placeSub = null;
        }
        if (this._pastTimer !== null) {
            clearTimeout(this._pastTimer);
            this._pastTimer = null;
        }
    }

    OnClearFilter(): void {
        if (!this.work) {
            return;
        }
        this.work.TableFilter = null;
        this._listDirty = true;
        this.change.markForCheck();
    }

    async OnSelectTicket(ticket: TicketView | null): Promise<void> {
        if (!ticket || !this.work) {
            return;
        }
        const copy = this.data.alives.get(ticket.Copy()) as TicketView;
        await this.work.OnPanel({
            main: 'view',
            scnd: 'ticket',
            args: { ticket: copy, mode: 'VIEW' },
        });
        this.change.markForCheck();
    }

    private _RebuildLists(): void {
        if (!this._listDirty) {
            return;
        }
        this._listDirty = false;
        const source = this.work
            ? this.work.OpenTickets(this.work.TableFilter)
            : [];
        this._recent = source.filter((t) => t.IsRecent);
        this._past = source.filter((t) => !t.IsRecent);
    }

    private _BindPlaceRefresh(): void {
        if (this._placeSub) {
            this._placeSub.unsubscribe();
            this._placeSub = null;
        }
        const place = this.place;
        if (!place) {
            return;
        }
        this._placeSub = place.OnViewRefresh.subscribe(() => {
            this._listDirty = true;
            this.change.markForCheck();
        });
    }

    /**
     * Mount past thumbs on the next macrotask so recent Ionic thumbs paint first
     * on the initial open of Comandas.
     */
    private _SchedulePastMount(): void {
        if (this._pastMounted || this._pastTimer !== null) {
            return;
        }
        this._pastTimer = setTimeout(() => {
            this._pastTimer = null;
            this._pastMounted = true;
            this.change.markForCheck();
        }, 0);
    }
}
